import { StepperOrientation, StepperSelectionEvent } from '@angular/cdk/stepper';
import { NgIf, AsyncPipe } from '@angular/common';
import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnInit, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatDivider } from '@angular/material/divider';
import { MatStepper, MatStepperIntl, MatStep, MatStepLabel } from '@angular/material/stepper';
import { LocationSilo, ProductSupplement } from '@next/core-lib/cart';
import { AbstractDialog, AbstractDialogData, AbstractDialogResultData, AgrDialogModule } from '@next/core-lib/dialog';
import { LocaleService, AgrI18nSharedModule } from '@next/core-lib/i18n';
import { BreakpointService, BreakpointServiceInjector } from '@next/core-lib/responsive';
import { UntilDestroy } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { tap } from 'rxjs/operators';

import { SiloProductItem } from 'src/app/components/silos/models/silo-product-list.models';

import { LineItem, ProductlistWizardTab, QuantityItem, SkuCatalogItemViewData } from '../../models/product.models';
import * as ProductActions from '../../store/actions';

import { ProductQuantityComponent } from '../product-quantity/product-quantity.component';
import { ProductSupplementComponent } from '../product-supplement/product-supplement.component';
import { ProductlistSiloComponent } from '../productlist-silo/productlist-silo.component';

const initialSteps = [
  { tab: ProductlistWizardTab.QUANTITY, title: 'product.wizard.quantity.tab-title' },
  { tab: ProductlistWizardTab.SILOS, title: 'product.wizard.silo.tab-title' },
  { tab: ProductlistWizardTab.PRODUCT_SUPPLEMENTS, title: 'product.wizard.supplement.tab-title' },
];

let steps = [...initialSteps];

interface ProductlistWizardDialogData extends AbstractDialogData {
  skuCatalogItem?: SkuCatalogItemViewData;
  silo: LocationSilo;
  source?: string;
}

@UntilDestroy({ checkProperties: true })
@Component({
  selector: 'app-productlist-wizard-dialog',
  templateUrl: './productlist-wizard-dialog.component.html',
  styleUrls: ['./productlist-wizard-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AgrDialogModule,
    NgIf,
    MatDivider,
    MatStepper,
    MatStep,
    MatStepLabel,
    ProductQuantityComponent,
    ProductlistSiloComponent,
    ProductSupplementComponent,
    AsyncPipe,
    AgrI18nSharedModule,
  ],
})
export class ProductlistWizardDialogComponent extends AbstractDialog<ProductlistWizardDialogData> implements OnInit, AfterViewInit {
  @ViewChild('wizardStepper') wizardStepper: MatStepper;

  public siloProductItem: SiloProductItem;
  public quantityItem: QuantityItem;
  public supplementLineItem: LineItem;

  isLtMd$ = this.breakpointService.ltMd$
    .pipe(
      tap((isLtMd) => {
        this.orientation = isLtMd ? 'vertical' : 'horizontal';
      }),
    )
    .subscribe();
  isLtSm$ = this.breakpointService.ltSm$;
  orientation: StepperOrientation;

  quantityIsValid = true;
  silosAreValid = true;
  silosAreOptional = true;
  productSupplementsIsValid = true;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: ProductlistWizardDialogData,
    protected mdDialogRef: MatDialogRef<ProductlistWizardDialogComponent, AbstractDialogResultData>,
    private readonly store: Store,
    private readonly matStepperIntl: MatStepperIntl,
    private readonly localeService: LocaleService,
    private readonly changeDetectorRef: ChangeDetectorRef,
    @Inject(BreakpointServiceInjector) private readonly breakpointService: BreakpointService,
  ) {
    super();

    this.matStepperIntl.optionalLabel = this.localeService.translate('product.wizard.optional');
    this.matStepperIntl.changes.next();

    this.siloProductItem = {
      id: this.data.skuCatalogItem.skuId,
      externalId: this.data.skuCatalogItem.externalSkuId,
      title: this.data.skuCatalogItem.title,
      orderingInformation: this.data.skuCatalogItem.orderingInformation,
      quantity: this.data.skuCatalogItem.quantity,
      selectedSiloIds: [this.data.silo.id],
      supplements: [],
      skuHasSupplements: this.data.skuCatalogItem.hasProductSupplements,
      lastDeliveryDate: null,
      submissionDate: null,
    };

    this.quantityItem = {
      quantity: this.data.skuCatalogItem.quantity,
      orderingInformation: this.data.skuCatalogItem.orderingInformation,
      packagingInformation: this.data.skuCatalogItem.packagingInformation,
    };

    this.supplementLineItem = {
      id: this.data.skuCatalogItem.skuId,
      title: this.data.skuCatalogItem.title,
      quantity: this.data.skuCatalogItem.quantity,
      orderUnit: this.data.skuCatalogItem.orderingInformation.orderUnit,
      productSupplements: this.siloProductItem.supplements,
    };

    this.updateSelectedProductSiloLine();
  }

  ngOnInit(): void {
    this.store.dispatch(ProductActions.getSilosRequest());
    this.store.dispatch(ProductActions.getSilosInUseOnDatesRequest());
  }

  ngAfterViewInit(): void {
    this.filterOutOptionalSteps();
    this.wizardStepper.selectedIndex = 0;
    this.data.disableCancelButton = true;
    this.data.disableAltButton = false;
    this.changeDetectorRef.detectChanges();
  }

  handleQuantityChange(quantity: number): void {
    this.siloProductItem = {
      ...this.siloProductItem,
      quantity,
    };
    this.supplementLineItem = {
      ...this.supplementLineItem,
      quantity,
    };
    this.updateSelectedProductSiloLine();
  }

  handleSilosChange(selectedSiloIds: number[]): void {
    this.siloProductItem = {
      ...this.siloProductItem,
      selectedSiloIds,
    };
    this.updateSelectedProductSiloLine();
  }

  handleProductSupplementsChange(supplements: ProductSupplement[]): void {
    this.siloProductItem = {
      ...this.siloProductItem,
      supplements,
    };
    this.updateSelectedProductSiloLine();
  }

  handleProductSupplementsIsValidChange(newProductSupplementsIsValid: boolean) {
    this.productSupplementsIsValid = this.data.skuCatalogItem.hasProductSupplements === true ? newProductSupplementsIsValid === true : true;
    this.updateDisableConfirmButton();
  }

  updateSelectedProductSiloLine(): void {
    this.resetSteps();
    this.store.dispatch(ProductActions.setSelectedProductSiloLine({ selectedProductSiloLine: this.siloProductItem }));
  }

  resetSteps() {
    steps = [...initialSteps];
  }

  handleStepperSelectionChange(selectionChange: StepperSelectionEvent): void {
    this.filterOutOptionalSteps();
    this.data.title = steps[selectionChange.selectedIndex].title;
    this.updateDialogFooterButtons(selectionChange.selectedIndex + 1);
  }

  handleQuantityIsValidChange(newQuantityIsValid: boolean): void {
    this.quantityIsValid = newQuantityIsValid;
    this.updateDisableConfirmButton();
  }

  handleSilosAreValidChange(newSilosAreValid: boolean): void {
    this.silosAreValid = newSilosAreValid;
    this.updateDisableConfirmButton();
  }

  stepBack(): void {
    this.wizardStepper.previous();
  }

  stepForward(): void {
    this.wizardStepper.next();
  }

  addToSilo(): void {
    if (!this.quantityIsValid) {
      return;
    }

    this.store.dispatch(
      ProductActions.addProductToSiloProductListRequest({
        request: {
          siloIds: this.siloProductItem.selectedSiloIds,
          skuId: this.siloProductItem.id,
          quantity: this.siloProductItem.quantity,
          supplements: this.siloProductItem.supplements,
        },
      }),
    );

    this.confirm();
  }

  private filterOutOptionalSteps() {
    if (!this.data.skuCatalogItem.hasProductSupplements) {
      steps = steps.filter((step) => step.tab !== ProductlistWizardTab.PRODUCT_SUPPLEMENTS);
    }
  }

  private updateDisableConfirmButton(): void {
    this.data = {
      ...this.data,
      disableConfirmButton: !this.quantityIsValid || !this.silosAreValid || !this.productSupplementsIsValid,
    };
  }

  private updateDialogFooterButtons(newSelectedIndex: number): void {
    this.data.disableCancelButton = newSelectedIndex === 1;
    this.data.disableAltButton = newSelectedIndex === this.wizardStepper.steps.length;
  }
}
